import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
	ApiError,
	ApiRequestError,
	callPlugin,
	uploadFile,
	type UploadedAttachment
} from '../../api/client';
import { saveDraft, sendMessage } from '../../api/mail';
import type { Message } from '../../api/types';
import { addressLine, displayName } from '../../api/types';
import { fullDate, fileSize } from '../../format';
import { sanitizeOutgoingHtml } from '../../mail/sanitize';
import { RichText, htmlToPlain, plainToHtml } from './RichText';
import { Button, Chip, Input } from '../../ui';

export interface Draft {
	to: string;
	cc: string;
	bcc: string;
	subject: string;
	/** Body as HTML. The plain-text alternative is derived on send. */
	html: string;
	inReplyTo?: string;
	references?: string;
}

interface Props {
	draft: Draft;
	from: string;
	sentFolder: string | null;
	draftsFolder: string | null;
	onClose: () => void;
	/** `warning` is set when the message went out but something after it did not. */
	onSent: (warning?: string) => void;
}

export function Composer({ draft, from, sentFolder, draftsFolder, onClose, onSent }: Props) {
	const [to, setTo] = useState(draft.to);
	const [cc, setCc] = useState(draft.cc);
	const [bcc, setBcc] = useState(draft.bcc);
	const [showCopies, setShowCopies] = useState(Boolean(draft.cc || draft.bcc));
	const [subject, setSubject] = useState(draft.subject);
	const [html, setHtml] = useState(draft.html);

	const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
	const [uploading, setUploading] = useState(0);

	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	const [savedAt, setSavedAt] = useState<string>('');

	const fileRef = useRef<HTMLInputElement>(null);
	// Where the draft currently lives, so re-saving replaces it instead of
	// appending another copy each time.
	const draftRef = useRef<{ folder: string; uid: number } | null>(null);

	/* ------------------------------------------------------------- signature */

	// Appended once, when the composer opens. The server builds the markup from
	// the three saved fields, so the signature is identical everywhere and the
	// browser never decides what it looks like.
	useEffect(() => {
		let cancelled = false;

		callPlugin<{ preview?: string }>('KeystoneSignatureGet')
			.then(result => {
				const signature = result?.preview;
				if (cancelled || !signature) {
					return;
				}
				setHtml(current => {
					if (current.includes('keystone-signature')) {
						return current;
					}
					// Above the quoted reply, which is where people expect to find it,
					// with a blank line so the caret has somewhere to land.
					return `<div><br></div><div class="keystone-signature">${signature}</div>${current}`;
				});
			})
			.catch(() => {
				// No signature configured, or the plugin is not installed. Composing
				// without one is fine; failing to compose would not be.
			});

		return () => {
			cancelled = true;
		};
	}, []);

	/* ----------------------------------------------------------- attachments */

	const addFiles = useCallback(async (files: FileList | null) => {
		if (!files?.length) {
			return;
		}
		setError('');
		setUploading(count => count + files.length);

		for (const file of Array.from(files)) {
			try {
				const uploaded = await uploadFile(file);
				setAttachments(current => [...current, uploaded]);
			} catch {
				setError(`${file.name} could not be attached. It may be too large.`);
			} finally {
				setUploading(count => count - 1);
			}
		}
	}, []);

	/* ----------------------------------------------------------- send / save */

	const payload = useCallback(
		() => {
			// Sanitized on the way out as well as the way in. The editor produces
			// this markup, but pasted content and link targets pass through it, and
			// nothing should leave here that we would refuse to render.
			const safeHtml = sanitizeOutgoingHtml(html);
			return {
				from,
				to,
				cc,
				bcc,
				subject,
				html: safeHtml,
				plain: htmlToPlain(safeHtml),
				inReplyTo: draft.inReplyTo,
				references: draft.references,
				attachments
			};
		},
		[from, to, cc, bcc, subject, html, draft.inReplyTo, draft.references, attachments]
	);

	async function submit(event: FormEvent) {
		event.preventDefault();
		if (sending || uploading > 0) {
			return;
		}

		setSending(true);
		setError('');

		try {
			await sendMessage({
				...payload(),
				sentFolder: sentFolder ?? '',
				// Clears the saved draft: the message now exists in Sent, and leaving
				// the draft behind means two copies of everything ever composed.
				draftFolder: draftRef.current?.folder,
				draftUid: draftRef.current?.uid
			});
			onSent();
		} catch (e: unknown) {
			// 301 is CantSaveMessage: raised AFTER the SMTP send succeeded, when only
			// filing the copy in Sent failed. Showing it as a send failure made people
			// send the same message again.
			if (e instanceof ApiRequestError && ApiError.CantSaveMessage === e.code) {
				onSent('Your message was sent, but a copy could not be saved to your Sent folder.');
				return;
			}
			setError(describeSendError(e));
		} finally {
			setSending(false);
		}
	}

	const save = useCallback(async () => {
		if (!draftsFolder) {
			return;
		}
		try {
			const saved = await saveDraft(
				{
					...payload(),
					draftFolder: draftRef.current?.folder,
					draftUid: draftRef.current?.uid
				},
				draftsFolder
			);
			draftRef.current = saved;
			setSavedAt(new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }));
		} catch {
			// Autosave failing silently is correct: it must never interrupt writing.
			// The explicit Save button reports its own failures.
		}
	}, [draftsFolder, payload]);

	// Autosave, idle-triggered rather than on an interval, so a long message is
	// not re-uploaded on a timer while somebody is still typing.
	const hasContent = Boolean(to || subject || htmlToPlain(html));
	useEffect(() => {
		if (!draftsFolder || !hasContent) {
			return;
		}
		const timer = setTimeout(() => void save(), 4000);
		return () => clearTimeout(timer);
	}, [to, cc, bcc, subject, html, draftsFolder, hasContent, save]);

	function discard() {
		if (hasContent && !window.confirm('Discard this message?')) {
			return;
		}
		onClose();
	}

	return (
		<form className="composer" onSubmit={submit}>
			<header className="pane__header">
				<h2 className="pane__title">{subject || 'New message'}</h2>
				<div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
					{savedAt && <span className="ks-muted" style={{ fontSize: 12 }}>Saved {savedAt}</span>}
					<Button variant="ghost" size="sm" onClick={discard}>Discard</Button>
				</div>
			</header>

			<div className="composer__fields">
				<div className="composer__row">
					<Input
						value={to}
						onChange={event => setTo(event.target.value)}
						placeholder="To"
						autoComplete="off"
						autoCapitalize="none"
						required
					/>
					{!showCopies && (
						<Button variant="ghost" size="sm" onClick={() => setShowCopies(true)}>
							Cc/Bcc
						</Button>
					)}
				</div>

				{showCopies && (
					<>
						<Input
							value={cc}
							onChange={event => setCc(event.target.value)}
							placeholder="Cc"
							autoComplete="off"
							autoCapitalize="none"
						/>
						<Input
							value={bcc}
							onChange={event => setBcc(event.target.value)}
							placeholder="Bcc"
							autoComplete="off"
							autoCapitalize="none"
						/>
					</>
				)}

				<Input
					value={subject}
					onChange={event => setSubject(event.target.value)}
					placeholder="Subject"
				/>
			</div>

			<RichText html={html} onChange={setHtml} placeholder="Write your message…" />

			{(attachments.length > 0 || uploading > 0) && (
				<div className="composer__attachments">
					{attachments.map(file => (
						<Chip
							key={file.tempName}
							meta={fileSize(file.size)}
							closeLabel={`Remove ${file.name}`}
							onClose={() =>
								setAttachments(current =>
									current.filter(item => item.tempName !== file.tempName)
								)
							}
						>
							{file.name}
						</Chip>
					))}
					{uploading > 0 && (
						<span className="ks-muted" style={{ fontSize: 13 }}>
							Attaching {uploading} file{1 === uploading ? '' : 's'}…
						</span>
					)}
				</div>
			)}

			<footer className="composer__footer">
				<Button type="submit" loading={sending} disabled={uploading > 0}>
					Send
				</Button>

				<input
					ref={fileRef}
					type="file"
					multiple
					hidden
					onChange={event => {
						void addFiles(event.target.files);
						// Reset so the same file can be picked twice in a row.
						event.target.value = '';
					}}
				/>
				<Button variant="outline" onClick={() => fileRef.current?.click()}>
					Attach
				</Button>

				{draftsFolder && (
					<Button variant="outline" onClick={() => void save()}>Save draft</Button>
				)}

				<span className="ks-muted" style={{ fontSize: 12 }}>from {from}</span>
				{error && <span className="ks-error">{error}</span>}
			</footer>
		</form>
	);
}

/* ------------------------------------------------------------ draft builders */

function quote(message: Message): string {
	const attribution = `On ${fullDate(message.dateTimestamp)}, ${displayName(message.from)} wrote:`;
	const body = message.html
		? sanitizeOutgoingHtml(message.html)
		: plainToHtml(message.plain ?? '');

	// A real <blockquote> rather than "> " prefixes, so replies nest the way every
	// other mail client renders them.
	return `<div><br></div><div>${attribution}</div><blockquote>${body}</blockquote>`;
}

const withPrefix = (subject: string, prefix: string): string =>
	new RegExp(`^${prefix}:`, 'i').test(subject.trim()) ? subject : `${prefix}: ${subject}`;

export function emptyDraft(): Draft {
	return { to: '', cc: '', bcc: '', subject: '', html: '' };
}

export function replyDraft(message: Message, all: boolean, self: string): Draft {
	const recipients = message.replyTo?.length ? message.replyTo : message.from;

	// Reply-all keeps the other recipients but never the sender themselves --
	// mailing yourself a copy of your own reply is nobody's intent.
	const others = all
		? [...(message.to ?? []), ...(message.cc ?? [])]
				.filter(item => item.email.toLowerCase() !== self.toLowerCase())
				.filter(item => !recipients.some(r => r.email.toLowerCase() === item.email.toLowerCase()))
		: [];

	return {
		to: addressLine(recipients),
		cc: addressLine(others),
		bcc: '',
		subject: withPrefix(message.subject || '', 'Re'),
		html: quote(message),
		inReplyTo: message.messageId ?? '',
		// Threading depends on References carrying the chain with the message being
		// replied to appended. Dropping it breaks threading in every client that
		// receives it, not just ours.
		references: [message.references, message.messageId].filter(Boolean).join(' ')
	};
}

export function forwardDraft(message: Message): Draft {
	const header = [
		'---------- Forwarded message ----------',
		`From: ${addressLine(message.from)}`,
		`Date: ${fullDate(message.dateTimestamp)}`,
		`Subject: ${message.subject}`,
		`To: ${addressLine(message.to)}`
	]
		.map(line => `<div>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`)
		.join('');

	const body = message.html
		? sanitizeOutgoingHtml(message.html)
		: plainToHtml(message.plain ?? '');

	return {
		to: '',
		cc: '',
		bcc: '',
		subject: withPrefix(message.subject || '', 'Fwd'),
		html: `<div><br></div>${header}<div><br></div>${body}`
	};
}

/** Plain-language reasons instead of "SendMessage failed (302)". */
function describeSendError(e: unknown): string {
	if (e instanceof ApiRequestError) {
		if (ApiError.AuthError === e.code || ApiError.InvalidToken === e.code) {
			return 'Your session has ended. Copy your message somewhere safe, then reload and sign in again.';
		}
		if (ApiError.CantSendMessage === e.code) {
			return 'The mail server would not send this message. Check the recipient addresses and try again.';
		}
	}
	if (e instanceof TypeError) {
		return 'You appear to be offline. Your draft is kept — try again once you are connected.';
	}
	return 'The message could not be sent. Please try again.';
}
